// Copyright © 2026 ZIONYX. All rights reserved.
// Unauthorized copying or distribution is prohibited.

/**
 * True se la stringa è SOLO emoji (1–3 grapheme), senza lettere/numeri/punteggiatura.
 * Rilevamento permissivo basato su code point (niente Unicode property esotiche).
 */
export function isOnlyEmoji(str: string | null | undefined): boolean {
  if (!str) return false
  // Rimuovi invisibili + ellissi pending UI + spazi ai bordi
  const clean = str
    .replace(/[\u200B\u200C\u2060\uFEFF\u00AD]/g, '')
    .replace(/\u2026/g, '')
    .replace(/…/g, '')
    .trim()
  if (!clean) return false

  // Qualsiasi lettera/cifra latina = testo normale (keycap gestite sotto)
  if (/[A-Za-z]/.test(clean)) return false

  let graphemes: string[]
  try {
    graphemes = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(clean)].map(
      (s) => s.segment,
    )
  } catch {
    graphemes = Array.from(clean)
  }
  if (graphemes.length < 1 || graphemes.length > 3) return false

  for (const g of graphemes) {
    if (!isEmojiGrapheme(g)) return false
  }
  return true
}

function isEmojiGrapheme(g: string): boolean {
  const t = g.trim()
  if (!t) return false
  // Keycap: 1️⃣ #️⃣ *️⃣
  if (/^[#*0-9]\uFE0F?\u20E3$/u.test(t)) return true
  // Niente ASCII stampabile (salvo sopra)
  if (/^[\x20-\x7E]+$/.test(t)) return false

  let hasEmojiCore = false
  for (const ch of t) {
    const cp = ch.codePointAt(0)!
    if (isEmojiRelatedCodePoint(cp)) {
      if (
        cp !== 0x200d &&
        cp !== 0xfe0f &&
        cp !== 0xfe0e &&
        cp !== 0x20e3 &&
        !(cp >= 0x1f3fb && cp <= 0x1f3ff)
      ) {
        hasEmojiCore = true
      }
      continue
    }
    // Char non ammesso in un grapheme emoji
    return false
  }
  return hasEmojiCore
}

function isEmojiRelatedCodePoint(cp: number): boolean {
  return (
    (cp >= 0x1f300 && cp <= 0x1faff) || // pictographs + supplemental
    (cp >= 0x1f1e6 && cp <= 0x1f1ff) || // flags
    (cp >= 0x1f600 && cp <= 0x1f64f) || // emoticons
    (cp >= 0x1f680 && cp <= 0x1f6ff) || // transport
    (cp >= 0x2600 && cp <= 0x27bf) || // misc + dingbats
    (cp >= 0x2300 && cp <= 0x23ff) || // technical
    (cp >= 0x2b50 && cp <= 0x2b55) ||
    (cp >= 0x2934 && cp <= 0x2935) ||
    (cp >= 0x25a0 && cp <= 0x25ff) || // geometric shapes (some emoji)
    (cp >= 0x2190 && cp <= 0x21ff) || // arrows (some emoji)
    cp === 0x00a9 ||
    cp === 0x00ae ||
    cp === 0x2122 ||
    cp === 0x3030 ||
    cp === 0x303d ||
    cp === 0x3297 ||
    cp === 0x3299 ||
    cp === 0x200d || // ZWJ
    cp === 0xfe0f ||
    cp === 0xfe0e ||
    cp === 0x20e3 || // combining keycap
    (cp >= 0x1f3fb && cp <= 0x1f3ff) || // skin tone
    (cp >= 0xe0020 && cp <= 0xe007f) // tag characters (subdivision flags)
  )
}
